// Realtime duplicate-login detection.
//
// The account is meant to be known by at most two people, so a second login is a security
// event: the backend revokes the older session immediately. Previously the evicted user only
// found out when they next clicked something, and each page reported it differently — a blank
// screen here, nothing at all there. This module gives that one owner:
//
//   1. a response interceptor, so ANY 401 carrying err_code SESSION_REVOKED signs the user out
//      the same way — that covers an active user on their very next request;
//   2. a 10s heartbeat, which covers an IDLE user whose tab is just sitting open;
//   3. a BroadcastChannel, so every tab of the same browser leaves together.
//
// All three funnel into one kickToLogin(), so there is exactly one way a session ends: a single
// dialog, shown over the page the user was already on, that they dismiss when they have read it.
import axios from "axios";

const HEARTBEAT_INTERVAL_MS = 10000;
// The heartbeat and the logout call both need a ceiling: neither has a spinner, and without
// one a hanging backend leaves requests stacking up (heartbeat) or the UI apparently frozen
// (logout) until the browser's own socket timeout fires.
const SESSION_REQUEST_TIMEOUT_MS = 8000;
const CHANNEL_NAME = "ctrlytics-session";
const LOGIN_ROUTE = "/";
const REVOKED_ERR_CODE = "SESSION_REVOKED";
const EXPIRED_ERR_CODE = "SESSION_EXPIRED";
const SUPERSEDED_REASON = "SUPERSEDED";
const IDLE_REASON = "EXPIRED_IDLE";
// Real user input, not the heartbeat, is what keeps a session alive. Pointer and key events
// cover normal use; "visible" counts because deliberately switching back to this tab is a
// person choosing to be here, and someone who then reads a report for half an hour without
// clicking is genuinely using the app.
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"];
const MODAL_ID = "ctrlytics-session-ended-modal";
const MODAL_STYLE_ID = "ctrlytics-session-ended-style";

// Ten in-flight requests failing at once must still produce ONE dialog.
let hasKicked = false;
let heartbeatTimer = null;
let channel = null;
// setInterval does not wait for the previous tick, and refocusing the tab fires an extra
// immediate check — without this, a slow backend accumulates overlapping heartbeats.
let isHeartbeatInFlight = false;
// Reset on every beat that reports it, so "active" always means "since the last report" rather
// than "at some point today".
let hasActivitySinceBeat = true;

function markActivity() {
  hasActivitySinceBeat = true;
}

const FALLBACK_TEXT = {
  supersededTitle: "Someone signed in as you",
  supersededBody:
    "This account allows one session at a time, so this one ended. Check the sign-in below.",
  idleTitle: "Signed out for inactivity",
  idleBody:
    "This session sat idle for {duration}. Nothing was lost \u2014 sign in again to carry on.",
  idleBodyPlain: "This session sat idle. Nothing was lost \u2014 sign in again to carry on.",
  expiredTitle: "Session expired",
  expiredBody: "Sessions end {duration} after signing in. Sign in again to carry on.",
  expiredBodyPlain: "This session is no longer valid. Sign in again to carry on.",
  fromLabel: "From",
  atLabel: "At",
  signInButton: "Sign in again",
  backButton: "Back to login",
  minute: "minute",
  minutes: "minutes",
  hour: "hour",
  hours: "hours",
  second: "second",
  seconds: "seconds",
};

function backendUrl(path) {
  return process.env.VUE_APP_BACKEND_URL + path;
}

function getToken() {
  try {
    return localStorage.getItem("token");
  } catch (e) {
    return null;
  }
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function clearCredentials() {
  // Session keys only — the user's language choice and preferences must survive.
  ["token", "bearer_token", "csrf_token", "role", "currentUser"].forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  });
}

function goToLogin() {
  // A full navigation rather than a router push: it guarantees no stale component keeps
  // polling with a dead token, whatever route the user was on.
  window.location.replace(LOGIN_ROUTE);
}

// "2026-08-20 12:02" from the backend's "2026-08-20 12:02:41". A plain trim rather than
// toLocaleString: this value is security evidence, and it must read the same to the person
// reporting it and the person reading the audit trail.
function formatAt(raw) {
  if (!raw) return "";
  const parts = String(raw).split(" ");
  return parts.length === 2 ? parts[0] + " " + parts[1].slice(0, 5) : String(raw);
}

// The window that lapsed, in words. "idle for 30 minutes" answers the question that
// "inactive for a while" only raises. Returns null when the backend sent no limit, and the
// caller then uses copy that makes no claim rather than inventing a number.
function formatDuration(seconds, s) {
  const total = Number(seconds);
  if (!total || total <= 0) return null;
  if (total >= 3600 && total % 3600 === 0) {
    const h = total / 3600;
    return h + " " + (h === 1 ? s.hour : s.hours);
  }
  if (total >= 60) {
    const m = Math.round(total / 60);
    return m + " " + (m === 1 ? s.minute : s.minutes);
  }
  return total + " " + (total === 1 ? s.second : s.seconds);
}

// One place decides what the dialog says, so the immediate English render and the localised
// re-render cannot drift apart. `kind` is "superseded" | "idle" | "expired".
function copyFor(kind, detail, s) {
  if (kind === "superseded") {
    return { title: s.supersededTitle, body: s.supersededBody, button: s.backButton };
  }
  const duration = formatDuration(detail && detail.limit_seconds, s);
  if (kind === "idle") {
    return {
      title: s.idleTitle,
      body: duration ? s.idleBody.replace("{duration}", duration) : s.idleBodyPlain,
      button: s.signInButton,
    };
  }
  return {
    title: s.expiredTitle,
    body: duration ? s.expiredBody.replace("{duration}", duration) : s.expiredBodyPlain,
    button: s.signInButton,
  };
}

// login.json carries one flat block per language. Missing keys fall back per key rather than
// per language, so a partly translated locale still shows everything it does have.
function stringsFor(data, langmode) {
  const t = data && data[langmode];
  if (!t) return null;
  const pick = (key, fallback) => t[key] || fallback;
  return {
    supersededTitle: pick("session_ended_title", FALLBACK_TEXT.supersededTitle),
    supersededBody: pick("session_ended_body", FALLBACK_TEXT.supersededBody),
    idleTitle: pick("session_idle_title", FALLBACK_TEXT.idleTitle),
    idleBody: pick("session_idle_body_timed", FALLBACK_TEXT.idleBody),
    idleBodyPlain: pick("session_idle_body", FALLBACK_TEXT.idleBodyPlain),
    expiredTitle: pick("session_expired_title", FALLBACK_TEXT.expiredTitle),
    expiredBody: pick("session_expired_body_timed", FALLBACK_TEXT.expiredBody),
    expiredBodyPlain: pick("session_expired_body", FALLBACK_TEXT.expiredBodyPlain),
    fromLabel: pick("session_ended_detail_label", FALLBACK_TEXT.fromLabel),
    atLabel: pick("session_ended_time_label", FALLBACK_TEXT.atLabel),
    backButton: pick("session_ended_button", FALLBACK_TEXT.backButton),
    signInButton: pick("session_signin_button", FALLBACK_TEXT.signInButton),
    minute: pick("session_unit_minute", FALLBACK_TEXT.minute),
    minutes: pick("session_unit_minutes", FALLBACK_TEXT.minutes),
    hour: pick("session_unit_hour", FALLBACK_TEXT.hour),
    hours: pick("session_unit_hours", FALLBACK_TEXT.hours),
    second: pick("session_unit_second", FALLBACK_TEXT.second),
    seconds: pick("session_unit_seconds", FALLBACK_TEXT.seconds),
  };
}

// Renders in English immediately, then swaps in the user's language if it is available. Doing
// it this way keeps the dialog instant — waiting on a fetch before showing a security notice
// would be the wrong trade.
function localiseModal(parts, kind, detail) {
  let langmode = "ENG";
  try {
    langmode = localStorage.getItem("lang") || "ENG";
  } catch (e) {
    // keep the default
  }
  if (langmode === "ENG") return;
  fetch("/login.json")
    .then((response) => response.json())
    .then((data) => {
      const s = stringsFor(data, langmode);
      if (!s) return;
      const copy = copyFor(kind, detail, s);
      parts.title.textContent = copy.title;
      parts.body.textContent = copy.body;
      parts.button.textContent = copy.button;
      if (parts.fromLabel) parts.fromLabel.textContent = s.fromLabel;
      if (parts.atLabel) parts.atLabel.textContent = s.atLabel;
    })
    .catch(() => {
      // English is already on screen.
    });
}

// Injected once. A stylesheet rather than inline styles because the button needs :hover,
// :active and :focus-visible — inline styles cannot express them.
//
// The card carries a coloured header band whose hue encodes what happened: deep blue for a
// routine sign-out, amber for a possible account takeover. Before this the two dialogs were
// near identical, so the security one read as housekeeping. Values are literal rather than
// theme tokens because this dialog must render correctly over any route, including one whose
// own stylesheet failed to load.
function ensureModalStyles() {
  if (document.getElementById(MODAL_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = MODAL_STYLE_ID;
  style.textContent = [
    "@keyframes ctrl-session-fade{from{opacity:0}to{opacity:1}}",
    "@keyframes ctrl-session-rise{from{opacity:0;transform:translateY(12px) scale(.97)}",
    "to{opacity:1;transform:translateY(0) scale(1)}}",
    ".ctrl-session-backdrop{position:fixed;inset:0;z-index:2147483647;display:flex;",
    "align-items:center;justify-content:center;padding:24px;background:rgba(0,0,0,.45);",
    "-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);",
    "animation:ctrl-session-fade .18s ease-out}",
    ".ctrl-session-card{width:100%;max-width:400px;background:#fff;border-radius:20px;",
    "overflow:hidden;box-shadow:0 18px 40px rgba(0,0,0,.28);font-family:inherit;",
    "color:#013042;animation:ctrl-session-rise .22s ease-out}",
    ".ctrl-session-band{display:flex;align-items:center;gap:12px;padding:20px 26px 18px;",
    "background:#013042}",
    ".ctrl-session-card--security .ctrl-session-band{background:#8a5a00}",
    ".ctrl-session-band svg{width:22px;height:22px;flex:none;color:#ffd600}",
    ".ctrl-session-title{margin:0;font-size:18px;font-weight:700;line-height:1.3;",
    "letter-spacing:-.01em;color:#fff}",
    ".ctrl-session-body{padding:22px 26px 26px;display:flex;flex-direction:column;gap:16px}",
    ".ctrl-session-text{margin:0;font-size:14px;line-height:1.55;color:#3d5a66}",
    ".ctrl-session-receipt{border:1px dashed #cfd8db;border-radius:10px;padding:12px 14px;",
    "display:flex;flex-direction:column;gap:8px}",
    ".ctrl-session-row{display:flex;justify-content:space-between;gap:16px;font-size:13px}",
    ".ctrl-session-key{font-size:10px;letter-spacing:.08em;text-transform:uppercase;",
    "color:#4a6572;align-self:center}",
    ".ctrl-session-val{color:#013042;font-variant-numeric:tabular-nums;word-break:break-word}",
    ".ctrl-session-btn{width:100%;padding:13px 20px;border:none;border-radius:12px;",
    "background:#ffd600;color:#013042;font-size:14px;font-weight:700;font-family:inherit;",
    "cursor:pointer;box-shadow:0 3px 6px rgba(1,48,66,.14);transition:all .2s ease}",
    ".ctrl-session-btn:hover{transform:translateY(-1px);background:#ebc600;",
    "box-shadow:0 6px 12px rgba(1,48,66,.18)}",
    ".ctrl-session-btn:active{transform:translateY(0);box-shadow:0 2px 4px rgba(1,48,66,.12)}",
    ".ctrl-session-btn:focus-visible{outline:2px solid #013042;outline-offset:2px}",
    "@media (prefers-reduced-motion:reduce){.ctrl-session-backdrop,.ctrl-session-card",
    "{animation:none}.ctrl-session-btn{transition:none}",
    ".ctrl-session-btn:hover{transform:none}}",
  ].join("");
  document.head.appendChild(style);
}

// Static markup with nothing interpolated into it — every server-supplied value reaches the
// DOM through textContent below, never through innerHTML.
const CLOCK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3.2 2"></path></svg>';
const SHIELD_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 9.5-4.1-1.9-7-5.3-7-9.5V6z"></path>' +
  '<path d="M12 9v4"></path><path d="M12 16.2v.1"></path></svg>';

function kindFor(reason) {
  if (reason === SUPERSEDED_REASON) return "superseded";
  return reason === IDLE_REASON ? "idle" : "expired";
}

function appendReceiptRow(receipt, label, value) {
  const row = document.createElement("div");
  row.className = "ctrl-session-row";
  const key = document.createElement("span");
  key.className = "ctrl-session-key";
  key.textContent = label;
  const val = document.createElement("span");
  val.className = "ctrl-session-val";
  val.textContent = value;
  row.appendChild(key);
  row.appendChild(val);
  receipt.appendChild(row);
  return key;
}

// Built in plain DOM rather than as a Vue component because it must be able to appear over any
// route, including ones whose own requests have just failed.
function showSessionEndedModal(reason, detail) {
  if (typeof document === "undefined" || document.getElementById(MODAL_ID)) return;
  ensureModalStyles();

  const kind = kindFor(reason);
  const isSuperseded = kind === "superseded";
  const copy = copyFor(kind, detail, FALLBACK_TEXT);

  // Dimmed and blurred, not blanked: a full white screen reads as a crash rather than a message.
  const backdrop = document.createElement("div");
  backdrop.id = MODAL_ID;
  backdrop.className = "ctrl-session-backdrop";

  const card = document.createElement("div");
  card.setAttribute("role", "alertdialog");
  card.setAttribute("aria-modal", "true");
  card.setAttribute("aria-labelledby", MODAL_ID + "-title");
  card.setAttribute("aria-describedby", MODAL_ID + "-body");
  card.className =
    "ctrl-session-card " +
    (isSuperseded ? "ctrl-session-card--security" : "ctrl-session-card--routine");

  const band = document.createElement("div");
  band.className = "ctrl-session-band";
  band.innerHTML = isSuperseded ? SHIELD_ICON : CLOCK_ICON;

  const title = document.createElement("h3");
  title.id = MODAL_ID + "-title";
  title.className = "ctrl-session-title";
  title.textContent = copy.title;
  band.appendChild(title);

  const body = document.createElement("div");
  body.className = "ctrl-session-body";

  const text = document.createElement("p");
  text.id = MODAL_ID + "-body";
  text.className = "ctrl-session-text";
  text.textContent = copy.body;
  body.appendChild(text);

  // The receipt is the security payload: it is how the account holder tells a colleague's
  // sign-in from an intruder's. Only the takeover case has anything to show.
  let fromLabel = null;
  let atLabel = null;
  if (isSuperseded && detail && (detail.ip || detail.at)) {
    const receipt = document.createElement("div");
    receipt.className = "ctrl-session-receipt";
    if (detail.ip) {
      fromLabel = appendReceiptRow(receipt, FALLBACK_TEXT.fromLabel, detail.ip);
    }
    if (detail.at) {
      atLabel = appendReceiptRow(receipt, FALLBACK_TEXT.atLabel, formatAt(detail.at));
    }
    body.appendChild(receipt);
  }

  const button = document.createElement("button");
  button.type = "button";
  button.className = "ctrl-session-btn";
  button.textContent = copy.button;
  // No countdown: this is a security notice, so it waits for the person to read it rather than
  // vanishing while they are away from the screen.
  button.addEventListener("click", goToLogin);
  body.appendChild(button);

  card.appendChild(band);
  card.appendChild(body);
  backdrop.appendChild(card);
  document.body.appendChild(backdrop);
  button.focus();

  localiseModal(
    { title: title, body: text, button: button, fromLabel: fromLabel, atLabel: atLabel },
    kind,
    detail
  );
}

export function kickToLogin(reason, detail, options) {
  const shouldBroadcast = !options || options.broadcast !== false;
  if (hasKicked) return;
  hasKicked = true;
  stopHeartbeat();
  clearCredentials();
  if (shouldBroadcast && channel) {
    try {
      channel.postMessage({ type: "revoked", reason: reason, detail: detail });
    } catch (e) {
      // ignore
    }
  }
  showSessionEndedModal(reason, detail);
}

// Revoked (someone else signed in) and expired (ran out of time) end the session the same way
// and differ only in what the dialog says, so one matcher covers both.
function isSessionEndedResponse(response) {
  return (
    response &&
    response.status === 401 &&
    response.data &&
    (response.data.err_code === REVOKED_ERR_CODE ||
      response.data.err_code === EXPIRED_ERR_CODE)
  );
}

async function checkSessionNow() {
  const token = getToken();
  if (!token || hasKicked || isHeartbeatInFlight) return;
  isHeartbeatInFlight = true;
  const wasActive = hasActivitySinceBeat;
  // Cleared BEFORE the request, not after: anything the user does while it is in flight belongs
  // to the next window, and clearing on completion would silently drop it.
  hasActivitySinceBeat = false;
  try {
    await axios.post(
      backendUrl("/v1/session/state"),
      { token: token, active: wasActive },
      { timeout: SESSION_REQUEST_TIMEOUT_MS }
    );
  } catch (e) {
    // A revoked session comes back as the 401 envelope and is handled by the interceptor
    // below. Anything else (offline, 5xx, timeout) is ignored on purpose — a network blip
    // must not sign anyone out.
  } finally {
    isHeartbeatInFlight = false;
  }
}

function startHeartbeat() {
  stopHeartbeat();
  if (hasKicked) return;
  heartbeatTimer = setInterval(checkSessionNow, HEARTBEAT_INTERVAL_MS);
}

function handleVisibilityChange() {
  if (!document.hidden) markActivity();
  if (document.hidden) {
    // A hidden tab cannot show a dialog, so polling it only burns requests.
    stopHeartbeat();
    return;
  }
  checkSessionNow();
  startHeartbeat();
}

function setupChannel() {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      const data = event && event.data;
      if (data && data.type === "revoked") {
        kickToLogin(data.reason, data.detail, { broadcast: false });
      }
    };
  } catch (e) {
    channel = null;
  }
}

// Ends the session server-side on a deliberate sign-out. Without this the row stays live and
// the user's OWN next login evicts it, raising a security event that never happened.
export function endSession() {
  const token = getToken();
  hasKicked = true; // our own sign-out must not also raise the "signed in elsewhere" dialog
  stopHeartbeat();
  if (!token) return Promise.resolve();
  return axios
    .post(
      backendUrl("/v1/session/logout"),
      { token: token },
      { timeout: SESSION_REQUEST_TIMEOUT_MS }
    )
    .catch(() => {});
}

// Called after a successful login: the app is an SPA, so without this the guard would stay
// disarmed for the rest of the page's life after a sign-out.
export function resetSessionGuard() {
  hasKicked = false;
  isHeartbeatInFlight = false;
  hasActivitySinceBeat = true;
  startHeartbeat();
}

export function attachSessionGuard(instance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isSessionEndedResponse(error.response)) {
        const data = error.response.data;
        kickToLogin(data.reason, data.detail);
      }
      return Promise.reject(error);
    }
  );
  return instance;
}

// Components import the global axios directly — cover them all, exactly as busyRetry does.
attachSessionGuard(axios);

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", handleVisibilityChange);
  // Passive: these listeners only observe, and must never delay scrolling or typing.
  ACTIVITY_EVENTS.forEach((name) => {
    document.addEventListener(name, markActivity, { passive: true, capture: true });
  });
  startHeartbeat();
  setupChannel();
}
